import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { useMemo, useRef, useState } from 'react';
import { NS } from '@/constants';
import { SEO_TYPES } from '@/constants/seo';
import { useAppDispatch, useAppSelector } from '@/redux';
import { selectStoreId } from '@/redux/slices/root.slice';
import { showStorageFilterModal } from '@/redux/slices/storage.slice';
import { useServices } from '@/services/useServices';
import { formatNMFilter, getSelectedFiltersInfos, parseFilters } from '@/utils/categUtils';
import { scrollToElement } from '@/utils/scroll';
import { fetchCategProducts } from './fetchCategProducts';

type UseCategViewDataParams = {
	products: any;
	categ: any;
	topCategs: any;
	nbTotalRows: number;
	page: number;
	sort: number;
	categId: string;
	initialFilterQuery: string;
};

export const useCategViewData = ({
	products,
	categ,
	topCategs,
	nbTotalRows,
	page,
	sort,
	categId,
	initialFilterQuery,
}: UseCategViewDataParams) => {
	const router = useRouter();
	const { t } = useTranslation([NS.COMMON, NS.CATEG, NS.CHECKOUT, NS.ALTER]);
	const { productsService } = useServices();
	const dispatch = useAppDispatch();

	const storeId = useAppSelector(selectStoreId);
	const storeIdForPrice = useAppSelector(({ gui }) => gui.storeIdForPrice);
	const locale = useAppSelector(({ root }) => root.storeConf.locale);
	const currency = useAppSelector(({ root }) => root.storeConf.currency);
	const loggedUser = useAppSelector(({ auth }) => auth.loggedUser);

	const topListingRef = useRef<any>();
	const isCUCategory = categId?.includes(SEO_TYPES.CATEG_CU_PREFIX_ID);

	const [viewData, setViewData] = useState<any>({ products, categ, topCategs, nbTotalRows, page, sort });
	const [filterQuery, setFilterQuery] = useState<string>(initialFilterQuery);
	const [isSwitchDisable, setIsSwitchDisable] = useState<boolean>(categ.isSpirit);
	const [selectedFiltersInfos, setSelectedFiltersInfos] = useState([]);

	const refreshView = async ({ page, sort, filterQuery, onlyCU, keepExtraParams = false, fromFilter = false }: any) => {
		scrollToElement(topListingRef, { behavior: 'instant', block: 'start', inline: 'start' });

		const {
			slug: _slug,
			spiritSlug: _spiritSlug,
			page: _page,
			sort: _sort,
			filter: _filter,
			searchTerm: _searchTerm,
			...otherExtraQuery
		} = router.query;
		const searchTerm = _searchTerm as string | undefined;
		const extraParams = keepExtraParams ? new URLSearchParams(otherExtraQuery as Record<string, string>).toString() : '';

		const queryParams = [
			searchTerm && `searchTerm=${searchTerm}`,
			page !== 1 && `page=${page}`,
			sort !== 1 && `sort=${sort}`,
			filterQuery !== '' && `filter=${filterQuery}`,
			extraParams,
		]
			.filter((p) => !!p)
			.join('&');

		let url: string = router.asPath.split('?')[0];

		if (queryParams) {
			url += `?${queryParams}`;
		}

		await router.push(url, url, { shallow: true });

		fetchCategProducts({
			productsService,
			storeId,
			categId,
			isCUCategory,
			onlyCU,
			page,
			sort,
			filterQuery,
			searchTerm,
			storeIdForPrice,
			ec: !!loggedUser?.isEc,
			withFilter: 'true',
		}).then((result: any) => {
			setViewData({
				...result,
				sort: parseInt(sort),
				page: parseInt(page),
				...(searchTerm ? {} : { filters: result.filters.filters }),
			});
			setSelectedFiltersInfos(
				getSelectedFiltersInfos(t, filterQuery, parseFilters(result.filters, filterQuery, storeId, t), locale, currency) ||
					[],
			);
			setFilterQuery(filterQuery);
			dispatch(showStorageFilterModal());
		});
	};

	const formattedViewData = useMemo(
		() => (viewData.filters ? { ...viewData, filters: formatNMFilter(t, viewData.filters) } : viewData),
		[viewData, t],
	);

	return {
		viewData: formattedViewData,
		setViewData,
		filterQuery,
		isSwitchDisable,
		setIsSwitchDisable,
		selectedFiltersInfos,
		topListingRef,
		isCUCategory,
		refreshView,
	};
};
